import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Tile-source kind. */
export type TileSourceKind = 'openfreemap' | 'maptiler' | 'mapbox' | 'pmtiles' | 'custom';

/**
 * Map settings — the only settings section that survived the UI purge; every
 * field here is consumed (buildStyle / useMapBase / trails). Add new fields
 * only alongside the code that reads them.
 */
export interface MapSettings {
  /** Which tile provider to use. */
  source: TileSourceKind;

  /** Online providers — base style URL or pre-defined styleId. */
  styleUrl: string;

  /** API key (MapTiler/Mapbox). */
  apiKey: string;

  /** PMTiles offline file URL (served by Hub at /tiles/region.pmtiles). */
  pmtilesUrl: string;

  /** Optional DEM PMTiles URL for 3D terrain. */
  demUrl: string;

  /** 3D terrain on/off. */
  terrain: boolean;
  terrainExaggeration: number;

  /** Default view when no camera is persisted yet. */
  initialLat: number;
  initialLon: number;
  initialZoom: number;
  initialPitch: number;

  /** Draw robot trails on the map. Read with `!== false` — older persisted
   *  settings objects predate the field and must default to visible. */
  showTrails?: boolean;

  /** Route basemap requests through the Hub's disk-caching /tilecache proxy
   *  (SPEC-0014) — fast repeat loads + offline once a region is warm. Read
   *  with `!== false` for the same legacy-persist reason as showTrails. */
  cacheTiles?: boolean;

  /** DEPRECATED — moved to `prefs.distanceUnit` (2026-07-14). Kept only so
   *  a previously persisted choice survives; read via `getDistanceUnit`. */
  distanceUnit?: 'm' | 'km';

  /** Cursor tooltip (lat/lon + distance/bearing from selection) on the map.
   *  Read with `!== false` — legacy persisted settings default to visible. */
  showHoverInfo?: boolean;

  /** When true, the home position tracks the pilot's browser geolocation —
   *  every position update rewrites initialLat/initialLon. */
  homeFollowsPilot?: boolean;

  /** Seconds of inactivity before GOTO (Direct) mode drops back to Measure
   *  so a stray map click can't fire an unintended GOTO. 0 = never. */
  directIdleExitS?: number;
  /** Show the countdown chip when this many seconds remain before the
   *  auto-switch. */
  directIdleWarnS?: number;

  /** Leg-label declutter: below this zoom the per-leg distance labels merge
   *  into one total per asset… */
  legLabelMergeZoom?: number;
  /** …and below this zoom no leg labels render at all. */
  legLabelHideZoom?: number;
}

/** App-wide operator preferences — not tied to any one workspace. */
export interface PrefsSettings {
  /** Unit for every distance readout in the app (map labels, telemetry,
   *  measure, hover). */
  distanceUnit: 'm' | 'km';
}

export interface StorageSettings {
  /** Byte budget for persisted trail history (MB). Oldest points are
   *  pruned after each commit once the estimate exceeds this. */
  trailBudgetMb: number;
}

interface SettingsStore {
  map: MapSettings;
  storage: StorageSettings;
  prefs: PrefsSettings;
  setMap: (m: Partial<MapSettings>) => void;
  setStorage: (s: Partial<StorageSettings>) => void;
  setPrefs: (p: Partial<PrefsSettings>) => void;
  reset: () => void;
}

export const DEFAULT_MAP_SETTINGS: MapSettings = {
  source: 'openfreemap',
  styleUrl: 'https://tiles.openfreemap.org/styles/liberty',
  apiKey: '',
  pmtilesUrl: '/tiles/region.pmtiles',
  demUrl: '',
  terrain: false,
  terrainExaggeration: 1.5,
  initialLat: 47.3977,
  initialLon: 8.5456,
  initialZoom: 14,
  initialPitch: 0,
  showTrails: true,
  cacheTiles: true,
  showHoverInfo: true,
  homeFollowsPilot: false,
  directIdleExitS: 60,
  directIdleWarnS: 30,
  legLabelMergeZoom: 13,
  legLabelHideZoom: 11,
};

export const DEFAULT_PREFS: PrefsSettings = {
  distanceUnit: 'm',
};

export const DEFAULT_STORAGE_SETTINGS: StorageSettings = {
  trailBudgetMb: 1024,
};

export const useSettings = create<SettingsStore>()(
  persist(
    (set) => ({
      map: DEFAULT_MAP_SETTINGS,
      storage: DEFAULT_STORAGE_SETTINGS,
      prefs: DEFAULT_PREFS,
      setMap: (m) => set((s) => ({ map: { ...s.map, ...m } })),
      setStorage: (st) => set((s) => ({ storage: { ...s.storage, ...st } })),
      setPrefs: (p) => set((s) => ({ prefs: { ...s.prefs, ...p } })),
      reset: () =>
        set({
          map: DEFAULT_MAP_SETTINGS,
          storage: DEFAULT_STORAGE_SETTINGS,
          prefs: DEFAULT_PREFS,
        }),
    }),
    {
      // Keep the v1 key so existing map prefs survive; the dead display /
      // connection sections that v1 also stored are simply ignored now.
      name: 'fabricos-settings-v1',
      version: 1,
    },
  ),
);

/** The app-wide distance unit. Falls back to the pre-2026-07-14 location
 *  (`map.distanceUnit`) so an operator's earlier choice survives the move. */
export function getDistanceUnit(s: {
  prefs?: PrefsSettings;
  map: MapSettings;
}): 'm' | 'km' {
  return s.prefs?.distanceUnit ?? s.map.distanceUnit ?? 'm';
}
